import { useState, useEffect, useCallback } from 'react';

export const useGameTimer = () => {
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [questionsInCurrentSession, setQuestionsInCurrentSession] = useState(0);
  const [duration, setDuration] = useState<number | null>(null); // in seconds

  useEffect(() => {
    let interval: number | undefined;

    if (isRunning) {
      interval = window.setInterval(() => {
        setTime(prevTime => {
          // If duration is set and we've reached it, stop the timer
          if (duration && prevTime >= duration) {
            setIsRunning(false);
            return prevTime;
          }
          return prevTime + 1;
        });
      }, 1000);
    }

    return () => {
      if (interval !== undefined) {
        clearInterval(interval);
      }
    };
  }, [isRunning, duration]);

  const toggleTimer = useCallback(() => {
    setIsRunning(prev => !prev);
  }, []);

  const resetTimer = useCallback(() => {
    setTime(0);
    setIsRunning(false);
    setQuestionsInCurrentSession(0);
  }, []);

  const setTimerDuration = useCallback((minutes: number) => {
    setDuration(minutes * 60);
    resetTimer();
  }, [resetTimer]);

  const incrementQuestions = useCallback(() => {
    if (isRunning) {
      setQuestionsInCurrentSession(prev => prev + 1);
    }
  }, [isRunning]);

  const getQuestionsPerMinute = useCallback(() => {
    if (time === 0) return 0;
    return (questionsInCurrentSession * 60 / time).toFixed(1);
  }, [time, questionsInCurrentSession]);

  const getRemainingTime = useCallback(() => {
    if (!duration) return null;
    return Math.max(0, duration - time);
  }, [duration, time]);

  return {
    time,
    isRunning,
    questionsInCurrentSession,
    duration,
    remainingTime: getRemainingTime(),
    toggleTimer,
    resetTimer,
    setTimerDuration,
    incrementQuestions,
    getQuestionsPerMinute,
  };
};